import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../api';
import { ArrowRight, ArrowLeft, X, Sparkle as Sparkles, Package, Users, ShoppingCart, GearSix as Settings } from '@phosphor-icons/react';

// Tour step configurations by role and business type
const getTourSteps = (role, businessType, isMobile) => {
  // Common welcome step
  const welcomeStep = {
    target: null,
    title: 'Welcome to Smart POS!',
    content: "Let's take a quick tour to help you get started with your new business management system.",
    placement: 'center',
    icon: '👋'
  };

  // Mobile-specific steps (no sidebar highlighting since it's a hamburger menu)
  if (isMobile) {
    const mobileOwnerSteps = [
      welcomeStep,
      {
        target: null,
        title: 'Dashboard',
        content: "Your business command center. See today's sales, revenue, and key metrics at a glance. Access it from the menu.",
        placement: 'center',
        icon: '📊'
      },
      {
        target: null,
        title: 'Point of Sale',
        content: 'Process sales, apply discounts, hold orders, and print receipts. Find it in the menu!',
        placement: 'center',
        icon: '💳'
      },
      {
        target: null,
        title: 'Inventory & Staff',
        content: 'Manage your products, track stock levels, and add team members from the menu.',
        placement: 'center',
        icon: '📦'
      },
      {
        target: null,
        title: 'Reports & Settings',
        content: 'View detailed analytics and customize your business settings anytime.',
        placement: 'center',
        icon: '⚙️'
      }
    ];

    const mobileCashierSteps = [
      welcomeStep,
      {
        target: null,
        title: 'Making Sales',
        content: 'Browse products, add them to cart, apply discounts, and complete checkout - all in one screen!',
        placement: 'center',
        icon: '💳'
      },
      {
        target: null,
        title: 'Hold & Resume',
        content: 'Need to pause? Hold the current order and resume it later when the customer returns.',
        placement: 'center',
        icon: '⏸️'
      }
    ];

    switch (role) {
      case 'OWNER':
      case 'ADMIN':
      case 'MANAGER':
        return mobileOwnerSteps;
      case 'CASHIER':
        return mobileCashierSteps;
      default:
        return [welcomeStep];
    }
  }

  // Desktop owner steps with sidebar targeting
  const ownerSteps = [
    welcomeStep,
    {
      target: '[data-tour="dashboard"]',
      title: 'Dashboard',
      content: "Your business command center. See today's sales, revenue, and key metrics at a glance.",
      placement: 'right',
      icon: '📊'
    },
    {
      target: '[data-tour="inventory"]',
      title: 'Inventory',
      content: 'Manage products, track stock levels, set prices, and organize categories.',
      placement: 'right',
      icon: '📦'
    },
    {
      target: '[data-tour="staff"]',
      title: 'Staff Management',
      content: 'Add team members, assign roles (Cashier, Manager, Kitchen), and manage permissions.',
      placement: 'right',
      icon: '👥'
    },
    {
      target: '[data-tour="pos"]',
      title: 'Point of Sale',
      content: 'Process sales, apply discounts, hold orders, and print receipts. This is where the magic happens!',
      placement: 'right',
      icon: '💳'
    },
    {
      target: '[data-tour="sales"]',
      title: 'Sales & Revenue',
      content: 'View transaction history, reprint receipts, and track your revenue over time.',
      placement: 'right',
      icon: '💰'
    },
    {
      target: '[data-tour="reports"]',
      title: 'Reports',
      content: 'Detailed analytics - sales trends, staff performance, product profitability, and more.',
      placement: 'right',
      icon: '📈'
    },
    {
      target: '[data-tour="settings"]',
      title: 'Settings',
      content: 'Customize your business - logo, tax rates, currency, daily email reports, and notifications.',
      placement: 'right',
      icon: '⚙️'
    }
  ];

  // Manager-specific steps
  const managerSteps = [
    welcomeStep,
    {
      target: '[data-tour="dashboard"]',
      title: 'Dashboard',
      content: 'Your daily overview - see sales performance and team activity.',
      placement: 'right',
      icon: '📊'
    },
    {
      target: '[data-tour="pos"]',
      title: 'Point of Sale',
      content: 'Process sales, handle discounts, and manage void requests.',
      placement: 'right',
      icon: '💳'
    },
    {
      target: '[data-tour="inventory"]',
      title: 'Inventory',
      content: 'Monitor stock levels, update products, and manage inventory.',
      placement: 'right',
      icon: '📦'
    },
    {
      target: '[data-tour="employees"]',
      title: 'Employees',
      content: 'View your team, track their activity, and monitor performance.',
      placement: 'right',
      icon: '👥'
    },
    {
      target: '[data-tour="sales"]',
      title: 'Sales History',
      content: 'View all transactions, reprint receipts, and handle returns.',
      placement: 'right',
      icon: '💰'
    }
  ];

  // Cashier-specific steps
  const cashierSteps = [
    welcomeStep,
    {
      target: '[data-tour="pos-products"]',
      title: 'Products',
      content: 'Browse or search products. Tap any item to add it to the cart.',
      placement: 'bottom',
      icon: '🛍️'
    },
    {
      target: '[data-tour="pos-cart"]',
      title: 'Shopping Cart',
      content: 'View items in the current sale. Adjust quantities or remove items here.',
      placement: 'left',
      icon: '🛒'
    },
    {
      target: '[data-tour="pos-hold"]',
      title: 'Hold Order',
      content: 'Save the current cart for later - perfect when a customer needs to step away.',
      placement: 'top',
      icon: '⏸️'
    },
    {
      target: '[data-tour="pos-discount"]',
      title: 'Discounts',
      content: 'Apply percentage or fixed discounts (manager approval may be required).',
      placement: 'top',
      icon: '🏷️'
    },
    {
      target: '[data-tour="pos-checkout"]',
      title: 'Checkout',
      content: 'Complete the sale - select payment method and print the receipt.',
      placement: 'top',
      icon: '✅'
    }
  ];

  // Kitchen staff steps
  const kitchenSteps = [
    welcomeStep,
    {
      target: '[data-tour="kds-orders"]',
      title: 'Incoming Orders',
      content: 'New orders appear here automatically. Tap an order to see details.',
      placement: 'bottom',
      icon: '📋'
    },
    {
      target: '[data-tour="kds-item"]',
      title: 'Order Items',
      content: 'Each item shows the dish name, quantity, and any special notes from the customer.',
      placement: 'bottom',
      icon: '🍽️'
    },
    {
      target: '[data-tour="kds-ready"]',
      title: 'Mark Ready',
      content: 'When food is ready, tap here to notify the server for pickup.',
      placement: 'top',
      icon: '🔔'
    }
  ];

  // Add restaurant-specific steps for relevant roles
  const restaurantAdditions = (baseSteps) => {
    if (businessType === 'FOOD_AND_BEVERAGE' || businessType === 'RESTAURANT') {
      if (role === 'CASHIER') {
        const tableStep = {
          target: '[data-tour="pos-tables"]',
          title: 'Table Selection',
          content: 'Select a table for dine-in orders, or skip for takeaway.',
          placement: 'left',
          icon: '🪑'
        };
        baseSteps.splice(1, 0, tableStep);
      }
    }
    return baseSteps;
  };

  switch (role) {
    case 'OWNER':
    case 'ADMIN':
      return restaurantAdditions([...ownerSteps]);
    case 'MANAGER':
      return restaurantAdditions([...managerSteps]);
    case 'CASHIER':
      return restaurantAdditions([...cashierSteps]);
    case 'KITCHEN':
      return kitchenSteps;
    default:
      return [welcomeStep];
  }
};

// Calculate position for tooltip
const calculatePosition = (targetRect, placement, tooltipSize, isMobile) => {
  const padding = isMobile ? 12 : 20;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  let top, left;

  // On mobile, always show tooltip at bottom of screen for better UX
  if (isMobile && placement !== 'center') {
    return {
      top: viewportHeight - tooltipSize.height - padding - 60, // Account for safe area
      left: padding,
      width: viewportWidth - (padding * 2)
    };
  }

  switch (placement) {
    case 'right':
      top = targetRect.top + (targetRect.height / 2) - (tooltipSize.height / 2);
      left = targetRect.right + padding;
      break;
    case 'left':
      top = targetRect.top + (targetRect.height / 2) - (tooltipSize.height / 2);
      left = targetRect.left - tooltipSize.width - padding;
      break;
    case 'top':
      top = targetRect.top - tooltipSize.height - padding;
      left = targetRect.left + (targetRect.width / 2) - (tooltipSize.width / 2);
      break;
    case 'bottom':
      top = targetRect.bottom + padding;
      left = targetRect.left + (targetRect.width / 2) - (tooltipSize.width / 2);
      break;
    case 'center':
    default:
      top = viewportHeight / 2 - tooltipSize.height / 2;
      left = viewportWidth / 2 - tooltipSize.width / 2;
      break;
  }

  // Keep tooltip within viewport
  const maxWidth = isMobile ? viewportWidth - (padding * 2) : 380;
  if (left < padding) left = padding;
  if (left + tooltipSize.width > viewportWidth - padding) {
    left = viewportWidth - tooltipSize.width - padding;
  }
  if (top < padding) top = padding;
  if (top + tooltipSize.height > viewportHeight - padding) {
    top = viewportHeight - tooltipSize.height - padding;
  }

  return { top, left, width: maxWidth };
};

// Tooltip Component - Floating card near target element with SVG mask highlighting
const TourTooltip = ({ step, stepIndex, totalSteps, onNext, onPrev, onSkip, onComplete }) => {
  const [targetRect, setTargetRect] = useState(null);
  const [viewportSize, setViewportSize] = useState({ width: window.innerWidth, height: window.innerHeight });
  const tooltipRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const isCentered = step.placement === 'center' || !step.target;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const updateTarget = () => {
      if (isCentered || !step.target) {
        setTargetRect(null);
        return;
      }

      const target = document.querySelector(step.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        setTargetRect(rect);

        // Scroll element into view if needed
        const elementTop = rect.top + window.scrollY;
        if (rect.top < 0 || rect.bottom > window.innerHeight) {
          window.scrollTo({
            top: elementTop - 120,
            behavior: 'smooth'
          });
        }
      } else {
        setTargetRect(null);
      }
    };

    // Initial update with delay for DOM
    const timer = setTimeout(updateTarget, 150);

    window.addEventListener('resize', updateTarget);
    window.addEventListener('scroll', updateTarget, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateTarget);
      window.removeEventListener('scroll', updateTarget, true);
    };
  }, [step, isCentered]);

  // Calculate tooltip position
  const getTooltipStyle = () => {
    const padding = 16;
    const tooltipWidth = isMobile ? Math.min(320, viewportSize.width - 32) : 360;

    // Centered positioning (no target)
    if (isCentered || !targetRect) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: tooltipWidth,
        maxWidth: 'calc(100vw - 32px)',
        zIndex: 9999
      };
    }

    // Position near target
    const tooltipHeight = 220;
    let top, left;

    if (isMobile) {
      // On mobile, center horizontally and position above/below target
      left = (viewportSize.width - tooltipWidth) / 2;
      const spaceBelow = viewportSize.height - targetRect.bottom;
      if (spaceBelow > tooltipHeight + padding) {
        top = targetRect.bottom + padding;
      } else {
        top = Math.max(padding, targetRect.top - tooltipHeight - padding);
      }
    } else {
      // Desktop positioning
      switch (step.placement) {
        case 'right':
          top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
          left = targetRect.right + padding;
          if (left + tooltipWidth > viewportSize.width - padding) {
            left = targetRect.left - tooltipWidth - padding;
          }
          break;
        case 'left':
          top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
          left = targetRect.left - tooltipWidth - padding;
          if (left < padding) {
            left = targetRect.right + padding;
          }
          break;
        case 'top':
          top = targetRect.top - tooltipHeight - padding;
          left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
          break;
        case 'bottom':
          top = targetRect.bottom + padding;
          left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
          break;
        default:
          top = targetRect.bottom + padding;
          left = targetRect.left;
      }

      // Keep within bounds
      if (left < padding) left = padding;
      if (left + tooltipWidth > viewportSize.width - padding) {
        left = viewportSize.width - tooltipWidth - padding;
      }
      if (top < padding) top = padding;
      if (top + tooltipHeight > viewportSize.height - padding) {
        top = viewportSize.height - tooltipHeight - padding;
      }
    }

    return {
      position: 'fixed',
      top: `${top}px`,
      left: `${left}px`,
      width: tooltipWidth,
      maxWidth: 'calc(100vw - 32px)',
      zIndex: 9999
    };
  };

  const progressPercent = ((stepIndex + 1) / totalSteps) * 100;

  // Calculate spotlight dimensions with padding
  const spotlightPadding = 8;
  const spotlightRect = targetRect ? {
    x: targetRect.left - spotlightPadding,
    y: targetRect.top - spotlightPadding,
    width: targetRect.width + spotlightPadding * 2,
    height: targetRect.height + spotlightPadding * 2,
  } : null;

  return createPortal(
    <>
      {/* SVG Overlay with spotlight cutout */}
      <div className="fixed inset-0 z-[9998] pointer-events-none">
        <svg
          className="absolute inset-0 w-full h-full"
          style={{ width: viewportSize.width, height: viewportSize.height }}
        >
          <defs>
            <mask id={`tour-spotlight-mask-${stepIndex}`}>
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spotlightRect && (
                <rect
                  x={spotlightRect.x}
                  y={spotlightRect.y}
                  width={spotlightRect.width}
                  height={spotlightRect.height}
                  rx="10"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(15, 23, 42, 0.85)"
            mask={`url(#tour-spotlight-mask-${stepIndex})`}
          />
        </svg>

        {/* Animated spotlight border */}
        {spotlightRect && (
          <div
            className="absolute rounded-xl transition-all duration-300 ease-out"
            style={{
              left: spotlightRect.x,
              top: spotlightRect.y,
              width: spotlightRect.width,
              height: spotlightRect.height,
              boxShadow: '0 0 0 3px rgba(59, 130, 246, 1), 0 0 0 6px rgba(59, 130, 246, 0.4), 0 0 25px rgba(59, 130, 246, 0.5)',
              animation: 'tour-pulse 2s ease-in-out infinite'
            }}
          />
        )}
      </div>

      {/* Floating Tooltip */}
      <div
        ref={tooltipRef}
        style={{
          ...getTooltipStyle(),
          animation: 'tooltip-float-in 0.35s ease-out forwards'
        }}
      >
        <div className="bg-primaryB-50 rounded-3xl shadow-elevated dark:shadow-elevated-dark overflow-hidden border border-primaryB-200/60">
          {/* Progress bar */}
          <div className="h-1 bg-primaryB-100">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-4 sm:p-5">
            {/* Header with icon */}
            <div className="flex items-start gap-3 mb-3">
              {step.icon && (
                <div className="w-11 h-11 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">{step.icon}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-bold text-lg sm:text-xl text-primaryB-900 truncate">{step.title}</h3>
                  <button
                    onClick={onSkip}
                    className="p-1.5 text-primaryB-400 hover:text-primaryB-600 hover:bg-primaryB-100 rounded-full transition-all duration-200 flex-shrink-0"
                    aria-label="Skip tour"
                  >
                    <X weight="bold" className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-xs text-blue-600 font-bold">
                  Step {stepIndex + 1} of {totalSteps}
                </span>
              </div>
            </div>

            <p className="text-primaryB-600 text-sm sm:text-base leading-relaxed mb-4">
              {step.content}
            </p>

            {/* Navigation */}
            <div className="flex items-center gap-2">
              {!isFirst ? (
                <button
                  onClick={onPrev}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-bold text-primaryB-600 hover:text-primaryB-900 bg-primaryB-100 hover:bg-primaryB-200 rounded-xl transition-all duration-200"
                >
                  <ArrowLeft weight="bold" className="w-3.5 h-3.5" />
                  Back
                </button>
              ) : (
                <button
                  onClick={onSkip}
                  className="px-3 py-2 text-sm font-semibold text-primaryB-400 hover:text-primaryB-600 transition-all duration-200"
                >
                  Skip
                </button>
              )}

              <div className="flex-1" />

              {isLast ? (
                <button
                  onClick={onComplete}
                  className="flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-sm shadow-card shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <Sparkles weight="bold" className="w-4 h-4" />
                  Get Started
                </button>
              ) : (
                <button
                  onClick={onNext}
                  className="flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-sm shadow-card shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                >
                  Next
                  <ArrowRight weight="bold" className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes tooltip-float-in {
          from {
            opacity: 0;
            transform: ${isCentered || !targetRect ? 'translate(-50%, -50%) scale(0.95)' : 'translateY(10px) scale(0.95)'};
          }
          to {
            opacity: 1;
            transform: ${isCentered || !targetRect ? 'translate(-50%, -50%) scale(1)' : 'translateY(0) scale(1)'};
          }
        }
        @keyframes tour-pulse {
          0%, 100% {
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 1), 0 0 0 6px rgba(59, 130, 246, 0.4), 0 0 25px rgba(59, 130, 246, 0.5);
          }
          50% {
            box-shadow: 0 0 0 4px rgba(99, 102, 241, 1), 0 0 0 8px rgba(99, 102, 241, 0.35), 0 0 35px rgba(99, 102, 241, 0.6);
          }
        }
      `}</style>
    </>,
    document.body
  );
};

// Quick Start Content Component - "What do you want to do first?"
const QuickStartContent = ({ onSelect, onClose, tenantSlug, role }) => {
  // Different actions based on role
  const getActions = () => {
    const baseUrl = `/${tenantSlug}`;

    switch (role) {
      case 'OWNER':
      case 'ADMIN':
        return [
          {
            key: 'products',
            label: 'Add my first product',
            description: 'Set up your inventory',
            icon: Package,
            path: `${baseUrl}/owner/products`,
            color: 'from-blue-500 to-blue-600'
          },
          {
            key: 'staff',
            label: 'Invite a team member',
            description: 'Add staff to your business',
            icon: Users,
            path: `${baseUrl}/owner/team`,
            color: 'from-purple-500 to-purple-600'
          },
          {
            key: 'sale',
            label: 'Make a test sale',
            description: 'Try out the POS system',
            icon: ShoppingCart,
            path: `${baseUrl}/owner/pos`,
            color: 'from-green-500 to-green-600'
          },
          {
            key: 'settings',
            label: 'Customize settings',
            description: 'Logo, currency, tax rates',
            icon: Settings,
            path: `${baseUrl}/owner/settings`,
            color: 'from-orange-500 to-orange-600'
          }
        ];

      case 'MANAGER':
        return [
          {
            key: 'sale',
            label: 'Start selling',
            description: 'Open the POS system',
            icon: ShoppingCart,
            path: `${baseUrl}/manager/pos`,
            color: 'from-green-500 to-green-600'
          },
          {
            key: 'inventory',
            label: 'Check inventory',
            description: 'View stock levels',
            icon: Package,
            path: `${baseUrl}/manager/inventory`,
            color: 'from-blue-500 to-blue-600'
          },
          {
            key: 'employees',
            label: 'View employees',
            description: 'See your team',
            icon: Users,
            path: `${baseUrl}/manager/employees`,
            color: 'from-purple-500 to-purple-600'
          }
        ];

      case 'CASHIER':
        return [
          {
            key: 'sale',
            label: 'Start selling',
            description: 'Open the POS system',
            icon: ShoppingCart,
            path: `${baseUrl}/dashboard`,
            color: 'from-green-500 to-green-600'
          }
        ];

      default:
        return [
          {
            key: 'sale',
            label: 'Get started',
            description: 'Open the main screen',
            icon: ShoppingCart,
            path: `${baseUrl}/dashboard`,
            color: 'from-green-500 to-green-600'
          }
        ];
    }
  };

  const actions = getActions();

  return (
    <div className="py-2">
      {/* Close button */}
      <div className="flex justify-end -mt-2 -mr-2 mb-2">
        <button
          onClick={onClose}
          className="p-2 text-primaryB-400 hover:text-primaryB-600 hover:bg-primaryB-100 rounded-full transition-all duration-200"
          aria-label="Close and explore on my own"
        >
          <X weight="bold" className="w-5 h-5" />
        </button>
      </div>

      <div className="text-center mb-5">
        <span className="text-4xl mb-3 block">🎉</span>
        <h2 className="text-xl font-bold text-primaryB-900 mb-1">You're All Set!</h2>
        <p className="text-primaryB-500 text-sm">
          What would you like to do first?
        </p>
      </div>

      <div className="space-y-2 mb-4">
        {actions.map(action => {
          const IconComponent = action.icon;
          return (
            <button
              key={action.key}
              onClick={() => onSelect(action.path)}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-primaryB-50 border-2 border-transparent hover:border-blue-200 hover:bg-blue-50/50 transition-all duration-200 group"
            >
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${action.color} flex items-center justify-center flex-shrink-0 shadow-card`}>
                <IconComponent weight="bold" className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 text-left">
                <span className="font-bold text-primaryB-900 block text-sm">{action.label}</span>
                <span className="text-xs text-primaryB-500">{action.description}</span>
              </div>
              <ArrowRight weight="bold" className="w-4 h-4 text-primaryB-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-200" />
            </button>
          );
        })}
      </div>

      <button
        onClick={onClose}
        className="w-full text-center text-sm text-primaryB-400 hover:text-primaryB-600 py-2 transition-all duration-200"
      >
        I'll explore on my own
      </button>
    </div>
  );
};

// Main OnboardingTour component
const OnboardingTour = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Track mobile state
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Check if user needs to see the tour
  useEffect(() => {
    if (user && !user.hasSeenTour && !user.isSuperAdmin) {
      const tourSteps = getTourSteps(user.role, user.businessType, isMobile);
      setSteps(tourSteps);

      // Delay to ensure DOM is ready
      const timer = setTimeout(() => {
        setIsRunning(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [user, isMobile]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, steps.length]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleComplete = useCallback(async () => {
    setIsRunning(false);
    // Show Quick Start for all roles (except Kitchen which goes straight to KDS)
    if (user?.role !== 'KITCHEN') {
      setShowQuickStart(true);
    } else {
      try {
        await usersAPI.updateProfile({ hasSeenTour: true });
        await refreshUser();
      } catch (error) {
        console.error('Failed to update tour status:', error);
      }
    }
  }, [user, refreshUser]);

  const handleSkip = useCallback(async () => {
    setIsRunning(false);
    try {
      await usersAPI.updateProfile({ hasSeenTour: true });
      await refreshUser();
    } catch (error) {
      console.error('Failed to update tour status:', error);
    }
  }, [refreshUser]);

  const handleQuickStartSelect = async (path) => {
    setShowQuickStart(false);
    try {
      await usersAPI.updateProfile({ hasSeenTour: true });
      await refreshUser();
      // Navigate to the selected page
      navigate(path);
    } catch (error) {
      console.error('Failed to update tour status:', error);
      // Still navigate even if API fails
      navigate(path);
    }
  };

  const handleQuickStartClose = async () => {
    setShowQuickStart(false);
    try {
      await usersAPI.updateProfile({ hasSeenTour: true });
      await refreshUser();
    } catch (error) {
      console.error('Failed to update tour status:', error);
    }
  };

  // Don't render if no user or tour already seen
  if (!user || user.hasSeenTour || user.isSuperAdmin) {
    return null;
  }

  return (
    <>
      {isRunning && steps.length > 0 && (
        <TourTooltip
          step={steps[currentStep]}
          stepIndex={currentStep}
          totalSteps={steps.length}
          onNext={handleNext}
          onPrev={handlePrev}
          onSkip={handleSkip}
          onComplete={handleComplete}
        />
      )}

      {/* Quick Start Modal */}
      {showQuickStart && createPortal(
        <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-primaryB-900/70 backdrop-blur-sm">
          <div
            className="bg-primaryB-50 rounded-2xl shadow-elevated dark:shadow-elevated-dark w-full max-w-md overflow-hidden"
            style={{ animation: 'tooltip-enter 0.3s ease-out forwards' }}
          >
            <div className="p-5">
              <QuickStartContent
                onSelect={handleQuickStartSelect}
                onClose={handleQuickStartClose}
                tenantSlug={user?.tenantSlug}
                role={user?.role}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default OnboardingTour;
